import { useState } from 'react';
import axios from 'axios';
import { Search, AlertCircle, CheckCircle2, XCircle, Clock, PauseCircle, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VERDICT_STYLE = {
  ACTIVE: { icon: CheckCircle2, tone: 'emerald', label: 'Active' },
  'PAST DUE': { icon: AlertCircle, tone: 'amber', label: 'Past due' },
  'CANCELLED — user request': { icon: XCircle, tone: 'rose', label: 'Cancelled by user' },
  'CANCELLED — via Paystack': { icon: XCircle, tone: 'rose', label: 'Cancelled on Paystack' },
  'AUTO-RENEW OFF': { icon: PauseCircle, tone: 'sky', label: 'Auto-renew off' },
  CANCELLED: { icon: XCircle, tone: 'rose', label: 'Cancelled' },
  INACTIVE: { icon: Clock, tone: 'stone', label: 'Inactive' },
  'NO ACCOUNT': { icon: AlertCircle, tone: 'stone', label: 'No account' },
};

const TONE_CLASSES = {
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  amber: 'bg-amber-50 border-amber-200 text-amber-900',
  rose: 'bg-rose-50 border-rose-200 text-rose-900',
  sky: 'bg-sky-50 border-sky-200 text-sky-900',
  stone: 'bg-stone-50 border-stone-200 text-stone-900',
};

const ACTION_LABELS = {
  reactivate: 'Reactivate',
  offer_discount: 'Offer a discount',
  send_retry_link: 'Send retry link',
  contact_user: 'Contact user',
  send_renewal_reminder: 'Send renewal reminder',
  invite_to_subscribe: 'Invite to subscribe',
};

export default function AdminBillingLookup() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [actioning, setActioning] = useState(null);  // which action-key is currently firing

  const lookup = async () => {
    const trimmed = (email || '').trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/admin/billing-lookup`, {
        params: { email: trimmed },
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Lookup failed';
      toast.error(detail);
      if (err?.response?.status === 403) {
        toast.error('Super-admin access required');
      }
    } finally {
      setLoading(false);
    }
  };

  const runAction = async (actionKey) => {
    if (!result?.email) return;

    // contact_user is a client-side mailto — no backend hop needed.
    if (actionKey === 'contact_user') {
      const subject = encodeURIComponent(`About your PlayFirst subscription`);
      window.open(`mailto:${result.email}?subject=${subject}`, '_self');
      return;
    }

    if (!window.confirm(
      `Send "${ACTION_LABELS[actionKey] || actionKey}" email to ${result.email}?`
    )) return;

    setActioning(actionKey);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API}/admin/billing-lookup/action`,
        { email: result.email, action: actionKey },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(r.data?.message || 'Sent');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Action failed';
      toast.error(detail);
    } finally {
      setActioning(null);
    }
  };

  const style = result ? (VERDICT_STYLE[result.verdict] || VERDICT_STYLE.INACTIVE) : null;
  const Icon = style?.icon;

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 space-y-6" data-testid="admin-billing-lookup">
      <div>
        <h1 className="text-2xl font-bold text-stone-900">Billing Lookup</h1>
        <p className="text-sm text-stone-600 mt-1">
          Paste an email to see whether the user unsubscribed, had a payment fail, or is still active — without touching MongoDB.
        </p>
      </div>

      <Card className="p-4 border-stone-200">
        <div className="flex gap-2">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') lookup(); }}
            placeholder="user@example.com"
            className="flex-1"
            data-testid="billing-lookup-email-input"
            autoFocus
          />
          <Button onClick={lookup} disabled={loading} data-testid="billing-lookup-submit">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            Look up
          </Button>
        </div>
      </Card>

      {result && (
        <Card
          className={`p-5 border-2 ${TONE_CLASSES[style?.tone] || TONE_CLASSES.stone}`}
          data-testid="billing-lookup-result-card"
        >
          <div className="flex items-start gap-4">
            {Icon && <Icon className="w-8 h-8 flex-shrink-0 mt-0.5" />}
            <div className="flex-1 space-y-3">
              <div>
                <div className="text-xs uppercase tracking-wide font-semibold opacity-70">
                  {result.email}
                </div>
                <div className="text-xl font-bold" data-testid="billing-lookup-verdict">
                  {result.headline}
                </div>
              </div>

              {result.detail_lines?.length > 0 && (
                <ul className="text-sm space-y-1">
                  {result.detail_lines.map((line, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="opacity-50">·</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              )}

              {result.user && (
                <div className="text-xs pt-2 border-t border-current opacity-70 space-y-0.5">
                  {result.user.name && <div><span className="font-semibold">Name:</span> {result.user.name}</div>}
                  <div><span className="font-semibold">Role:</span> {result.user.role || '—'}</div>
                  {result.user.parent_plan_key && (
                    <div><span className="font-semibold">Plan:</span> {result.user.parent_plan_key}</div>
                  )}
                  {result.user.created_at && (
                    <div><span className="font-semibold">Signed up:</span> {result.user.created_at}</div>
                  )}
                </div>
              )}

              {result.actions?.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {result.actions.map((a) => {
                    const busy = actioning === a;
                    return (
                      <button
                        key={a}
                        type="button"
                        onClick={() => runAction(a)}
                        disabled={busy || actioning !== null}
                        className="text-xs px-3 py-1.5 rounded-full border-2 border-current bg-white/60 hover:bg-white transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                        data-testid={`billing-action-${a}`}
                      >
                        {busy && <Loader2 className="w-3 h-3 animate-spin" />}
                        {ACTION_LABELS[a] || a}
                      </button>
                    );
                  })}
                  {/* Always offer 'Contact user' as an escape hatch */}
                  <button
                    type="button"
                    onClick={() => runAction('contact_user')}
                    className="text-xs px-3 py-1.5 rounded-full border-2 border-current bg-white/60 hover:bg-white transition-colors font-semibold"
                    data-testid="billing-action-contact-user"
                  >
                    Email them directly
                  </button>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {result?.recent_failures?.length > 0 && (
        <Card className="p-4 border-stone-200" data-testid="billing-lookup-failures">
          <h3 className="text-sm font-semibold text-stone-900 mb-2">Recent payment failures</h3>
          <ul className="text-xs space-y-1 text-stone-700">
            {result.recent_failures.map((f, i) => {
              const inv = f.invoice_data || {};
              const amt = inv.amount ? `${inv.currency || 'ZAR'} ${(inv.amount / 100).toFixed(2)}` : '—';
              return (
                <li key={i} className="flex justify-between gap-4">
                  <span>{(f.timestamp || '').slice(0, 19).replace('T', ' ')} UTC</span>
                  <span className="font-mono">{amt}</span>
                  <span className="text-stone-500 truncate flex-1">{inv.gateway_response || inv.status || '—'}</span>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {result?.unmatched_events?.length > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50" data-testid="billing-lookup-unmatched">
          <h3 className="text-sm font-semibold text-amber-900 mb-1">
            {result.unmatched_events.length} unmatched Paystack event(s)
          </h3>
          <p className="text-xs text-amber-800">
            Paystack sent activity for this email but we couldn&apos;t match it to a PlayFirst account.
            Head to <a href="/admin/parent-members" className="underline font-medium">Parent Members → Unmatched billing events</a> to review.
          </p>
        </Card>
      )}
    </div>
  );
}
